import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, KeyRound, Loader2, AlertTriangle, Building2 } from "lucide-react";

const RULES = "Au moins 12 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial.";

export default function Activate() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | valid | error
  const [info, setInfo] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    if (!token) { setState("error"); setErrMsg("Lien d'activation invalide."); return; }
    api.get(`/activation/validate?token=${encodeURIComponent(token)}`)
      .then(({ data }) => { if (active) { setInfo(data); setState("valid"); } })
      .catch((e) => { if (active) { setState("error"); setErrMsg(formatApiError(e.response?.data?.detail)); } });
    return () => { active = false; };
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault();
    if (pw !== pw2) { toast.error("Les mots de passe ne correspondent pas"); return; }
    setBusy(true);
    try {
      await api.post("/activation/complete", { token, new_password: pw });
      toast.success("Compte activé");
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground px-4">
      <div className="w-full max-w-md border border-border bg-card rounded-sm p-8" data-testid="activate-card">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="h-9 w-9 rounded-sm bg-primary flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-white" />
          </div>
          <div className="font-head font-extrabold text-lg">CROS</div>
        </div>

        {state === "checking" && (
          <div className="flex items-center gap-2 text-muted-foreground mt-6" data-testid="activate-checking">
            <Loader2 className="h-5 w-5 animate-spin" /> Vérification du lien…
          </div>
        )}

        {state === "error" && (
          <div className="mt-6" data-testid="activate-error">
            <h1 className="font-head text-xl font-bold flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-5 w-5" /> Lien indisponible
            </h1>
            <p className="text-sm text-muted-foreground mt-2">{errMsg}</p>
            <Button variant="outline" className="rounded-sm mt-5" onClick={() => navigate("/login")} data-testid="activate-go-login">
              Aller à la connexion
            </Button>
          </div>
        )}

        {state === "valid" && (
          <>
            <h1 className="font-head text-xl font-bold mt-4 flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" /> Activer votre compte CISO
            </h1>
            <div className="mt-3 text-sm text-muted-foreground space-y-1">
              <div className="flex items-center gap-2"><Building2 className="h-4 w-4" /> {info?.tenant_name}</div>
              <div className="font-mono text-xs">{info?.ciso_email}</div>
            </div>
            <form onSubmit={submit} className="space-y-4 mt-6">
              <div>
                <Label className="text-xs">Nouveau mot de passe</Label>
                <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1 rounded-sm" data-testid="activate-password-input" />
              </div>
              <div>
                <Label className="text-xs">Confirmer</Label>
                <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1 rounded-sm" data-testid="activate-confirm-input" />
              </div>
              <p className="text-[11px] text-muted-foreground">{RULES}</p>
              <Button type="submit" className="w-full rounded-sm" disabled={!pw || !pw2 || busy} data-testid="activate-submit-button">
                {busy ? "Activation…" : "Activer et se connecter"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
